"use client"

import { useEffect, useState } from "react"
import { Loader2 } from "lucide-react"
import { toast } from "sonner"

import { useUpdateWebWidgetPrimaryColorMutation } from "@/hooks/mutations/messaging-channels"
import { WIDGET_DEFAULT_CONFIG } from "@/lib/agents/messaging/channels/web-widget/constants"

type WebWidgetColorPickerProps = {
  channelId: string
  primaryColor?: string
  canManage: boolean
  onRefresh: () => void
  onError: (message: string | null) => void
}

export function WebWidgetColorPicker({
  channelId,
  primaryColor,
  canManage,
  onRefresh,
  onError,
}: WebWidgetColorPickerProps) {
  const savedColor = primaryColor ?? WIDGET_DEFAULT_CONFIG.primaryColor
  const [color, setColor] = useState(savedColor)
  const updateColor = useUpdateWebWidgetPrimaryColorMutation()

  useEffect(() => {
    setColor(savedColor)
  }, [savedColor])

  const handleChange = async (nextColor: string) => {
    const previousColor = color
    setColor(nextColor)
    onError(null)

    try {
      await updateColor.mutateAsync({
        channelId,
        primaryColor: nextColor,
      })
      toast.success("Chat color updated", {
        description: "Changes may take up to 15 minutes to appear on your website.",
      })
      onRefresh()
    } catch (error) {
      setColor(previousColor)
      onError(
        error instanceof Error ? error.message : "Failed to update chat color",
      )
    }
  }

  return (
    <label
      className="relative block h-7 w-7 shrink-0 overflow-hidden rounded-full border-2 border-background shadow-sm ring-1 ring-border"
      style={{ backgroundColor: color }}
      title="Chat color"
    >
      <span className="sr-only">Chat color</span>
      <input
        type="color"
        aria-label="Chat color"
        value={color}
        onChange={(event) => void handleChange(event.currentTarget.value)}
        disabled={!canManage || updateColor.isPending}
        className="absolute inset-0 h-full w-full cursor-pointer opacity-0 disabled:cursor-not-allowed"
      />
      {updateColor.isPending ? (
        <span className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/20">
          <Loader2 className="h-3.5 w-3.5 animate-spin text-white" />
        </span>
      ) : null}
    </label>
  )
}
